// =============================================================================
// BP-05-03: Calendar Frontend - CalendarSidebar Component
// =============================================================================

import React, { useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Filter,
  X,
  Download,
} from 'lucide-react';
import {
  AppointmentType,
  AppointmentStatus,
  APPOINTMENT_TYPE_CONFIG,
} from '../types';
import { useCalendarStore } from '../stores/calendarStore';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

interface CalendarSidebarProps {
  children?: Array<{ id: string; name: string }>;
  onCreateAppointment?: () => void;
  onExport?: () => void;
  className?: string;
}

// -----------------------------------------------------------------------------
// Mini Calendar Component
// -----------------------------------------------------------------------------

interface MiniCalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  highlightedDates?: Date[];
}

function MiniCalendar({ selectedDate, onDateSelect, highlightedDates = [] }: MiniCalendarProps) {
  const [viewMonth, setViewMonth] = useState(selectedDate);

  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const isHighlighted = (date: Date) =>
    highlightedDates.some((d) => isSameDay(d, date));

  return (
    <div className="bg-white rounded-xl p-4 shadow-soft">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setViewMonth(subMonths(viewMonth, 1))}
          className="p-1.5 hover:bg-cream rounded-lg transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-navy" />
        </button>
        <span className="font-medium text-navy">
          {format(viewMonth, 'MMMM yyyy')}
        </span>
        <button
          onClick={() => setViewMonth(addMonths(viewMonth, 1))}
          className="p-1.5 hover:bg-cream rounded-lg transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-navy" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <div
            key={i}
            className="h-8 flex items-center justify-center text-xs font-medium text-warm-gray"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Days */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const isCurrentMonth = isSameMonth(day, viewMonth);
          const isSelected = isSameDay(day, selectedDate);
          const isTodayDate = isToday(day);
          const hasEvents = isHighlighted(day);

          return (
            <button
              key={day.toISOString()}
              onClick={() => {
                onDateSelect(day);
                setViewMonth(day);
              }}
              className={cn(
                'h-8 w-8 flex items-center justify-center text-sm rounded-full transition-colors relative',
                !isCurrentMonth && 'text-warm-gray/40',
                isCurrentMonth && !isSelected && 'text-navy hover:bg-cream',
                isSelected && 'bg-sage text-white',
                isTodayDate && !isSelected && 'font-bold text-sage'
              )}
            >
              {format(day, 'd')}
              {hasEvents && !isSelected && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-terracotta" />
              )}
            </button>
          );
        })}
      </div>

      {/* Today Button */}
      <button
        onClick={() => {
          const today = new Date();
          onDateSelect(today);
          setViewMonth(today);
        }}
        className="w-full mt-3 py-2 text-sm font-medium text-sage hover:bg-sage/10 rounded-lg transition-colors"
      >
        Today
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Filter Section Component
// -----------------------------------------------------------------------------

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
}

function FilterSection({ title, children }: FilterSectionProps) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold text-warm-gray uppercase tracking-wider">
        {title}
      </h4>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

export function CalendarSidebar({
  children = [],
  onCreateAppointment,
  onExport,
  className,
}: CalendarSidebarProps) {
  const {
    currentDate,
    selectedChildId,
    selectedAppointmentTypes,
    selectedStatuses,
    setCurrentDate,
    selectDate,
    setSelectedChildId,
    toggleAppointmentType,
    toggleStatus,
    clearFilters,
  } = useCalendarStore();

  const hasActiveFilters =
    selectedChildId !== null ||
    selectedAppointmentTypes.length > 0 ||
    selectedStatuses.length > 0;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Create Button */}
      <button
        onClick={onCreateAppointment}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-sage text-white rounded-xl font-medium hover:bg-sage-dark transition-colors shadow-soft"
      >
        <Plus className="w-5 h-5" />
        New Appointment
      </button>

      {/* Mini Calendar */}
      <MiniCalendar
        selectedDate={currentDate}
        onDateSelect={(date) => {
          setCurrentDate(date);
          selectDate(date);
        }}
      />

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-soft space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-navy flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-sage hover:text-sage-dark transition-colors"
            >
              Clear all
            </button>
          )}
        </div>

        {/* Child Filter */}
        {children.length > 0 && (
          <FilterSection title="Child">
            <select
              value={selectedChildId || ''}
              onChange={(e) => setSelectedChildId(e.target.value || null)}
              className="w-full px-3 py-2 bg-cream rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage/20"
            >
              <option value="">All Children</option>
              {children.map((child) => (
                <option key={child.id} value={child.id}>
                  {child.name}
                </option>
              ))}
            </select>
          </FilterSection>
        )}

        {/* Type Filter */}
        <FilterSection title="Type">
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(APPOINTMENT_TYPE_CONFIG).map(([type, config]) => (
              <button
                key={type}
                onClick={() => toggleAppointmentType(type as AppointmentType)}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                  selectedAppointmentTypes.includes(type as AppointmentType)
                    ? 'bg-sage text-white'
                    : 'bg-cream text-navy hover:bg-cream-dark'
                )}
              >
                {config.label}
              </button>
            ))}
          </div>
        </FilterSection>

        {/* Status Filter */}
        <FilterSection title="Status">
          <div className="flex flex-wrap gap-1.5">
            {Object.values(AppointmentStatus).map((status) => (
              <button
                key={status}
                onClick={() => toggleStatus(status)}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-medium capitalize transition-colors',
                  selectedStatuses.includes(status)
                    ? 'bg-sage text-white'
                    : 'bg-cream text-navy hover:bg-cream-dark'
                )}
              >
                {status.replace('_', ' ')}
              </button>
            ))}
          </div>
        </FilterSection>
      </div>

      {/* Export Button */}
      <button
        onClick={onExport}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-navy rounded-xl font-medium hover:bg-cream transition-colors shadow-soft border border-warm-gray/10"
      >
        <Download className="w-4 h-4" />
        Export Calendar
      </button>
    </div>
  );
}


export default CalendarSidebar;
